/**
 * Unsaved document recovery — UI layer.
 *
 * Two ways in, because people arrive with two different things: the file
 * itself, or no idea where it is. The folder route walks a chosen folder and
 * finds every recoverable artefact in it; the file route takes one .asd, .wbk,
 * .tmp or .doc directly.
 */

import { readDoc, DocError } from './doc-text.js';
import { looksLikeCfb } from './cfb-reader.js';

const T = (k, p) => {
  let s = (window.__UD_I18N && window.__UD_I18N[k]) || k;
  if (p) for (const a of Object.keys(p).sort((x, y) => y.length - x.length)) {
    s = s.split(':' + a).join(p[a]);
  }
  return s;
};
const el = (id) => document.getElementById(id);
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

function human(bytes) {
  const u = [T('unit_b'), T('unit_kb'), T('unit_mb'), T('unit_gb')];
  let n = Number(bytes) || 0, i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n < 10 && i ? n.toFixed(1) : Math.round(n)} ${u[i]}`;
}

const state = { results: [] };

function show(html) { const o = el('ud-out'); o.innerHTML = html; o.hidden = false; }
const card = (tone, title, body, extra = '') =>
  `<div class="ud-card ud-tone-${tone}"><p class="ud-title">${title}</p>
     <p class="ud-body">${body}</p>${extra}</div>`;

/** What each extension means, so the report names the thing rather than the suffix. */
const KIND_BY_EXT = {
  asd: 'autorecover', wbk: 'backup', doc: 'doc', dot: 'doc',
  tmp: 'temp', asw: 'autorecover', xlk: 'excel_backup',
};
const kindOf = (name) => KIND_BY_EXT[(String(name).split('.').pop() || '').toLowerCase()] || 'other';

/** Files worth even opening. Everything else in a folder is noise. */
const CANDIDATE = /\.(asd|asw|wbk|doc|dot|tmp|xlk)$/i;
const isCandidate = (name) => CANDIDATE.test(name) || /^~WR|^~\$/.test(name);

async function readOne(file) {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const row = { name: file.name, size: file.size, kind: kindOf(file.name) };

  if (!looksLikeCfb(bytes)) {
    /* `~$name.docx` is an OWNER file — a few dozen bytes holding who has the
     * document open. People find them and hope; saying so beats an error. */
    row.error = /^~\$/.test(file.name) && file.size < 4096 ? 'owner_file' : 'not_cfb';
    return row;
  }
  try {
    const res = readDoc(bytes);
    if (!res.chars) { row.error = 'no_text'; return row; }
    Object.assign(row, {
      text: res.text, paragraphs: res.paragraphs, chars: res.chars,
      declaredChars: res.declaredChars, nFib: res.nFib,
    });
  } catch (err) {
    row.error = err instanceof DocError ? err.code : 'error';
  }
  return row;
}

async function handle(files) {
  const list = [...files].filter((f) => isCandidate(f.name));
  const skipped = files.length - list.length;
  if (!list.length) {
    show(card('warn', T('verdict_nothing_to_open'), T('body_nothing_to_open')));
    return;
  }

  show(`<div class="ud-working">${T('reading')}</div>`);
  window.__stage && window.__stage('reading');

  const rows = [];
  for (const f of list) {
    const w = document.querySelector('.ud-working');
    if (w) w.textContent = T('reading_n', { name: f.name, done: String(rows.length + 1), total: String(list.length) });
    rows.push(await readOne(f));
  }
  state.results = rows;
  render(rows, skipped);

  /* The outcome, not just the visit. Without this a recovered document is
   * invisible: the page-view says someone arrived, and nothing says whether
   * they left with their text. `kinds` answers the question that decides what
   * to build next — which of .asd / .wbk / .doc / ~WRD people actually bring. */
  const good = rows.filter((r) => r.chars);
  const kinds = {};
  for (const r of rows) kinds[r.kind] = (kinds[r.kind] || 0) + 1;
  window.__stage && window.__stage('analyzed');
  if (good.length) {
    window.__aeToolComplete && window.__aeToolComplete({
      files: rows.length, recovered: good.length,
      chars: good.reduce((n, r) => n + r.chars, 0),
      kinds: Object.keys(kinds).join(','),
    });
  } else {
    /* "Nothing opened" is the answer we most need to see: it is either a real
     * limit of the format or a bug, and the reason codes tell them apart. */
    window.__aeDiagnostic && window.__aeDiagnostic({
      issue: 'no_text',
      reasons: [...new Set(rows.map((r) => r.error).filter(Boolean))].join(','),
      files: rows.length, skipped,
    });
  }
}

const ERROR_KEY = {
  owner_file: 'err_owner_file', not_cfb: 'err_not_cfb', no_text: 'err_no_text',
  encrypted: 'err_encrypted', not_a_doc: 'err_not_a_doc', no_word_stream: 'err_no_word_stream',
};

function render(rows, skipped) {
  const good = rows.filter((r) => r.chars);
  const bad = rows.filter((r) => !r.chars);
  good.sort((a, b) => b.chars - a.chars);

  if (!good.length) {
    show(card('bad', T('verdict_none'), T('body_none'))
      + failuresBlock(bad, skipped));
    return;
  }

  const cards = good.map((r, i) => `
    <section class="ud-doc" data-i="${i}">
      <header class="ud-doc-head">
        <div>
          <p class="ud-doc-name"><code>${esc(r.name)}</code>
             <span class="ud-kind">${T(`kind_${r.kind}`)}</span></p>
          <p class="ud-doc-meta">${T('doc_meta', {
            chars: String(r.chars), paras: String(r.paragraphs.length), size: human(r.size) })}</p>
        </div>
        <div class="ud-doc-actions">
          <button type="button" class="ud-mini" data-docx="${i}">DOCX</button>
          <button type="button" class="ud-mini" data-txt="${i}">TXT</button>
        </div>
      </header>
      <div class="ud-preview">${r.paragraphs.slice(0, 12).map((p) =>
        `<p>${esc(p.length > 400 ? `${p.slice(0, 400)}…` : p)}</p>`).join('')}
        ${r.paragraphs.length > 12 ? `<p class="ud-more">${T('more_paragraphs', { n: String(r.paragraphs.length - 12) })}</p>` : ''}
      </div>
    </section>`).join('');

  show(card('ok', T('verdict_found', { n: String(good.length) }),
      T('body_found', { chars: String(good.reduce((n, r) => n + r.chars, 0)) }))
    + `<div class="ud-docs">${cards}</div>`
    + failuresBlock(bad, skipped));

  wire(good);
}

function failuresBlock(bad, skipped) {
  if (!bad.length && !skipped) return '';
  const items = bad.map((r) =>
    `<li><code>${esc(r.name)}</code> — ${T(ERROR_KEY[r.error] || 'err_other')}</li>`).join('');
  return `<section class="ud-block"><h3>${T('failed_title', { n: String(bad.length + skipped) })}</h3>
    ${items ? `<ul class="ud-list">${items}</ul>` : ''}
    ${skipped ? `<p class="ud-body">${T('skipped_desc', { n: String(skipped) })}</p>` : ''}</section>`;
}

function wire(good) {
  /* The unit is the RECOVERY, not each download: taking the same document as
   * DOCX and then as TXT is one job. */
  let charged = false;
  const bill = () => {
    if (charged || !window.__toolLimit) { charged = true; return { commit() {}, abort() {} }; }
    return window.__toolLimit.begin('unsaved-doc');
  };

  const nameFor = (r, ext) => `${String(r.name).replace(/\.[^.]+$/, '') || 'document'}-recovered.${ext}`;

  document.querySelectorAll('[data-docx]').forEach((b) => b.addEventListener('click', async () => {
    const run = bill();
    if (!run) return;
    const r = good[Number(b.getAttribute('data-docx'))];
    // The .docx writer is the site's, not a second implementation: a hand-rolled
    // "Word HTML" file is what mobile Word refuses to open.
    window.__stage && window.__stage('exporting');
    try {
      const blob = await window.DocxWriter.build({ title: '', paragraphs: r.paragraphs });
      window.__downloadFile(blob, nameFor(r, 'docx'));
      run.commit();
      window.__aeToolComplete && window.__aeToolComplete({ export: 'docx', chars: r.chars, kind: r.kind });
    } catch (err) {
      // A failed export is the one thing the person will call broken, so it has
      // to reach the dashboard rather than dying in the console.
      run.abort();
      window.__aeToolError && window.__aeToolError({ errorType: 'docx_build', message: String(err).slice(0, 120) });
      window.__handleError && window.__handleError(err, 'unsaved-doc/docx');
    }
  }));

  document.querySelectorAll('[data-txt]').forEach((b) => b.addEventListener('click', () => {
    const run = bill();
    if (!run) return;
    const r = good[Number(b.getAttribute('data-txt'))];
    // A BOM so Notepad and Excel read the Cyrillic instead of guessing at it.
    const blob = new Blob(['\uFEFF' + r.paragraphs.join('\r\n\r\n')], { type: 'text/plain;charset=utf-8' });
    window.__downloadFile(blob, nameFor(r, 'txt'));
    run.commit();
  }));
}

async function pickFolder() {
  if (typeof window.showDirectoryPicker !== 'function') return;
  let dir;
  try { dir = await window.showDirectoryPicker({ mode: 'read', id: 'unsaved-doc' }); } catch (_) { return; }

  show(`<div class="ud-working">${T('scanning')}</div>`);
  const files = [];
  const walk = async (handle, depth) => {
    if (depth > 3 || files.length > 500) return;
    try {
      for await (const [name, child] of handle.entries()) {
        if (child.kind === 'directory') { await walk(child, depth + 1); continue; }
        if (!isCandidate(name)) continue;
        try { files.push(await child.getFile()); } catch (_) { /* unreadable entry */ }
      }
    } catch (_) { /* an unlistable folder is not fatal here */ }
  };
  await walk(dir, 0);
  if (!files.length) {
    show(card('warn', T('verdict_folder_empty'), T('body_folder_empty')));
    return;
  }
  await handle(files);
}

document.addEventListener('DOMContentLoaded', () => {
  const zone = el('udDrop');
  if (zone) {
    zone.addEventListener('tool-uploader:file', (ev) => {
      const d = ev.detail || {};
      const files = d.files && d.files.length ? [...d.files] : (d.file ? [d.file] : []);
      if (files.length) handle(files);
    });
  }
  const btn = el('ud-folder');
  if (btn) {
    /* The button ships hidden and is REVEALED here — the earlier version only
     * ever set `hidden = true`, so on the browsers that do support the picker
     * it stayed invisible and the whole folder-search route was unreachable.
     * Assigning the boolean makes it correct in both directions. */
    btn.hidden = typeof window.showDirectoryPicker !== 'function';
    if (!btn.hidden) btn.addEventListener('click', pickFolder);
  }
});

export { handle };
